function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
// eslint-disable-next-line no-restricted-imports

import { useCallback, useMemo, useState } from 'react';

/**
 * Subset of React Stately ListData + bulkUpdate.
 */

/**
 * Manages state associated with an immutable list of data.
 *
 * This hook is a revised version of React Stately's
 * `useListData` hook with a couple of improvements.
 * - Added support for bulk update
 * - Proper memoization of the return object + its members
 * - Using Iterable instead of spread function arguments to avoid stack overflow
 *   for large lists
 * - findItem - method to look for item without throwing an exception if it isn't found
 *
 * @param options.getKey Optional function to derive a key from an item in the list
 */
export function useWindowedListData() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$getKey = _ref.getKey,
    getKey = _ref$getKey === void 0 ? defaultGetKey : _ref$getKey;
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    items = _useState2[0],
    setItems = _useState2[1];
  var _useState3 = useState(() => new Set()),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedKeys = _useState4[0],
    setSelectedKeys = _useState4[1];

  /** Determine if key matches an item's key */
  var matchKey = useCallback(key => item => getKey(item) === key, [getKey]);

  /** Search for item with given key. Returns null if not found. */
  var findItem = useCallback(key => {
    var _items$find;
    return (_items$find = items.find(matchKey(key))) !== null && _items$find !== void 0 ? _items$find : null;
  }, [items, matchKey]);

  /**
   * Get item with the given key. Throws an error if matching item does not exist.
   * Use `findItem` to return null instead of throwing an error.
   */
  var getItem = useCallback(key => {
    var item = findItem(key);
    if (item == null) {
      throw new Error("No item found matching key: ".concat(key));
    }
    return item;
  }, [findItem]);

  /** Append items to the end of the list */
  var append = useCallback(values => {
    setItems(prevItems => [...prevItems, ...values]);
  }, []);

  /** Insert items starting at the given index */
  var insert = useCallback((index, values) => {
    setItems(prevItems => [...prevItems.slice(0, index), ...values, ...prevItems.slice(index)]);
  }, []);

  /** Remove items with the given keys */
  var remove = useCallback(keys => {
    var keySet = new Set(keys);
    setItems(prevItems => prevItems.filter(item => !keySet.has(getKey(item))));
  }, [getKey]);

  /** Put a given item in the slot corresponding to a given key */
  var update = useCallback((key, item) => {
    var i = items.findIndex(matchKey(key));
    if (i === -1) {
      return;
    }
    setItems(prevItems => [...prevItems.slice(0, i), item, ...prevItems.slice(i + 1)]);
  }, [items, matchKey]);

  /**
   * Bulk update items from a map of keys to items. It is optimized to only
   * re-create the list 1x.
   */
  var bulkUpdate = useCallback(itemMap => {
    if (itemMap.size === 0) {
      return;
    }
    var indices = [];
    var indexMap = new Map();

    // Build a sorted array of indices for keys we want to update + a map
    // of those indices to their respective keys.
    // eslint-disable-next-line no-restricted-syntax
    for (var key of itemMap.keys()) {
      var i = items.findIndex(matchKey(key));
      indices.push(i);
      indexMap.set(i, key);
    }
    indices.sort((a, b) => a - b);
    var newItems = [];

    // Build a new items array replacing any items corresponding to our
    // indices array.
    items.forEach((item, i) => {
      if (indices[0] === i) {
        /* eslint-disable @typescript-eslint/no-non-null-assertion */
        var _key = indexMap.get(indices.shift());
        var newItem = itemMap.get(_key);
        /* eslint-enable @typescript-eslint/no-non-null-assertion */

        newItems.push(newItem);
      } else {
        newItems.push(item);
      }
    });
    setItems(newItems);
  }, [items, matchKey]);
  var listData = useMemo(() => ({
    items,
    selectedKeys,
    append,
    bulkUpdate,
    findItem,
    getItem,
    insert,
    remove,
    setItems,
    setSelectedKeys,
    update
  }), [append, bulkUpdate, findItem, getItem, insert, items, remove, setItems, selectedKeys, update]);
  return listData;
}

/**
 * Default getKey function simply returns item.key if the property exists. If
 * not, an error is thrown.
 * @param item
 */
export function defaultGetKey(item) {
  var hasKey = item != null && typeof item === 'object' && 'key' in item;
  if (!hasKey) {
    throw new Error('Item does not have a `key` prop.');
  }
  return item.key;
}
export default useWindowedListData;
//# sourceMappingURL=useWindowedListData.js.map